import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

const AnimatedCounter = ({ value }) => {
    const motionValue = useMotionValue(0);

    // Intelligently parse strings like "21 (0%)" or "1,234"
    const match = String(value).match(/^([\d.,]+)(.*)$/);
    const numStr = match ? match[1].replace(/,/g, '') : '';
    const numericValue = match ? parseFloat(numStr) : NaN;
    const suffixStr = match ? match[2] : '';
    
    useEffect(() => {
        if (!isNaN(numericValue)) {
            const controls = animate(motionValue, numericValue, {
                duration: 1.2,
                ease: "easeOut"
            });
            return controls.stop;
        }
    }, [numericValue, motionValue]);

    const rounded = useTransform(motionValue, (latest) => {
        const val = Math.round(latest);
        return val.toLocaleString();
    });

    if (!match || isNaN(numericValue)) return <React.Fragment>{value}</React.Fragment>;
    return (
        <React.Fragment>
            <motion.span>{rounded}</motion.span>
            {suffixStr && <span>{suffixStr}</span>}
        </React.Fragment>
    );
};

export function MetricsCards({ metrics = [], visibleIds: externalVisibleIds, onVisibleIdsChange, onMetricClick, maxVisible = 6 }) {
    const [internalVisibleIds, setInternalVisibleIds] = useState([]);
    const [draftVisibleIds, setDraftVisibleIds] = useState([]);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const settingsRef = useRef(null);
    const discardRef = useRef(null);

    const activeVisibleIds = externalVisibleIds || internalVisibleIds;

    useEffect(() => {
        if (!externalVisibleIds && metrics.length > 0 && internalVisibleIds.length === 0) {
            const initial = metrics.slice(0, maxVisible).map(m => m.id);
            setInternalVisibleIds(initial);
            setDraftVisibleIds(initial);
        }
    }, [metrics, internalVisibleIds.length, externalVisibleIds, maxVisible]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (showDiscardModal && discardRef.current && !discardRef.current.contains(event.target)) return;
            if (!showDiscardModal && isSettingsOpen && settingsRef.current && !settingsRef.current.contains(event.target)) {
                handleCloseAttempt();
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        
        const handleCustomize = () => {
            if (!isSettingsOpen) setDraftVisibleIds([...activeVisibleIds]);
            setIsSettingsOpen(prev => !prev);
        };
        window.addEventListener('oblique-customize-layout', handleCustomize);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('oblique-customize-layout', handleCustomize);
        };
    }, [activeVisibleIds, isSettingsOpen, showDiscardModal]);

    const toggleMetric = (id) => {
        setDraftVisibleIds(prev => {
            if (prev.includes(id)) return prev.filter(vId => vId !== id);
            if (prev.length >= maxVisible) return prev;
            return [...prev, id];
        });
    };

    const handleCloseAttempt = () => {
        const hasChanges = JSON.stringify([...draftVisibleIds].sort()) !== JSON.stringify([...activeVisibleIds].sort());
        if (hasChanges) setShowDiscardModal(true);
        else setIsSettingsOpen(false);
    };

    const handleSave = () => {
        if (onVisibleIdsChange) onVisibleIdsChange(draftVisibleIds);
        else setInternalVisibleIds(draftVisibleIds);
        setIsSettingsOpen(false);
    };

    const handleDiscard = () => {
        setDraftVisibleIds([...activeVisibleIds]);
        setShowDiscardModal(false);
        setIsSettingsOpen(false);
    };

    const visibleMetrics = metrics.filter(m => activeVisibleIds.includes(m.id));

    if (metrics.length === 0) return null;

    return (
        <div className="relative w-full pb-4 box-border">
            {/* Layout Settings Modal */}
            <AnimatePresence>
                {isSettingsOpen && (
                    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[9999] flex items-center justify-center">
                        <motion.div 
                            ref={settingsRef}
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4 w-80 shadow-2xl"
                        >
                            <div className="flex items-center mb-4 gap-2">
                                <button onClick={handleCloseAttempt} className="bg-transparent border-none cursor-pointer text-[var(--text-tertiary)] flex items-center">
                                    <ObliqueIcon name="arrow_back" size={18} />
                                </button>
                                <div className="text-[15px] font-semibold text-[var(--text-primary)]">Layout</div>
                                <div className="ml-auto text-[12px] text-[var(--text-tertiary)]">({draftVisibleIds.length}/{maxVisible})</div>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-2 mb-4">
                                {metrics.map(m => {
                                    const isSelected = draftVisibleIds.includes(m.id);
                                    const isDisabled = !isSelected && draftVisibleIds.length >= maxVisible;
                                    return (
                                        <div 
                                            key={m.id} 
                                            onClick={() => !isDisabled && toggleMetric(m.id)}
                                            className={`
                                                flex flex-col items-center justify-center gap-2 p-3 rounded-lg border transition-all duration-150
                                                ${isSelected ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]' : 'border-[var(--border)] bg-transparent text-[var(--text-secondary)]'}
                                                ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[var(--bg-subtle)]'}
                                            `}
                                        >
                                            <ObliqueIcon name={m.icon || 'analytics'} size={20} />
                                            <span className="text-[12px] font-medium text-center leading-tight">{m.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={handleCloseAttempt} className="flex-1 p-2 rounded-md bg-[var(--bg-muted)] border border-[var(--border)] text-[var(--text-primary)] text-[13px] font-medium cursor-pointer hover:bg-[var(--bg-subtle)] transition-colors">
                                    Cancel
                                </button>
                                <button onClick={handleSave} className="flex-1 p-2 rounded-md bg-[var(--accent)] border-none text-white text-[13px] font-medium cursor-pointer hover:opacity-90 transition-opacity">
                                    Apply
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Discard Modal */}
            <AnimatePresence>
                {showDiscardModal && (
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[10000] flex items-center justify-center">
                        <motion.div 
                            ref={discardRef}
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-6 w-80 shadow-2xl text-center"
                        >
                            <div className="text-[16px] font-semibold text-[var(--text-primary)] mb-6">Discard all layout changes?</div>
                            <div className="flex flex-col gap-2">
                                <button onClick={handleDiscard} className="w-full p-2.5 rounded-md bg-[var(--danger)] border-none text-white text-[14px] font-medium cursor-pointer hover:opacity-90 transition-opacity">
                                    Discard
                                </button>
                                <button onClick={() => setShowDiscardModal(false)} className="w-full p-2.5 rounded-md bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--text-primary)] text-[14px] font-medium cursor-pointer hover:bg-[var(--bg-subtle)] transition-colors">
                                    Cancel
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Metrics Grid */}
            <div className="flex items-center gap-3 overflow-x-auto w-full box-border no-scrollbar">
                {visibleMetrics.map((stat, i) => {
                    // We use CSS variables to pass dynamic colors cleanly into Tailwind
                    const metricColor = stat.color === 'var(--text-primary)' ? 'var(--text-secondary)' : (stat.color || 'var(--accent)');
                    
                    return (
                        <motion.div 
                            key={stat.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                            onClick={() => onMetricClick && onMetricClick(stat)}
                            style={{ '--metric-color': metricColor }}
                            className={`
                                flex-1 px-3 py-2 rounded-[10px] border flex flex-col gap-1 transition-all duration-200 min-w-[110px] text-left
                                ${onMetricClick ? 'cursor-pointer hover:bg-[var(--bg-subtle)]' : 'cursor-default'}
                                ${stat.isActive ? 'border-[color:var(--metric-color)] bg-[color:var(--metric-color)]/10 shadow-[0_4px_12px_rgba(0,0,0,0.05)]' : 'border-[var(--border)] bg-[var(--bg-page)] shadow-sm'}
                            `}
                        >
                            <div className="flex items-center justify-between w-full">
                                <span className={`text-[11px] font-semibold opacity-80 uppercase tracking-wide text-[color:var(--metric-color)]`}>
                                    {stat.label}
                                </span>
                                <ObliqueIcon name={stat.icon || 'auto_graph'} size={14} className={`opacity-50 text-[color:var(--metric-color)]`} />
                            </div>
                            <span className={`text-[18px] font-bold ${stat.isActive ? 'text-[color:var(--metric-color)]' : 'text-[var(--text-primary)]'}`}>
                                <AnimatedCounter value={stat.value} />
                                {stat.suffix && <span className="text-[14px] opacity-60 ml-1">{stat.suffix}</span>}
                            </span>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
}
